import { useRef, useLayoutEffect } from 'react';
import { useDetectFocusRemoval } from '../hooks/use-detect-focus-removal.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { isPortaledEvent } from '../utils/event.js';
import { isFocusWithin, isDefaultElementFocused, findClosestFocusableIgnoringDisplayNone, findClosestFocusable } from '../utils/focus.js';
import { getFocusReturnCandidates, LastInteraction } from '../utils/interaction.js';

function useFocusAndReturn({ ref: refIn, onBlur: onBlurIn, ...props }, { open = true, ignoreDisplayNone, onBeforeFocusReturn } = {}) {
    const returnCandidates = useRef(undefined);
    const rootRef = useRef(null);
    useLayoutEffect(() => {
        if (!open) {
            returnCandidates.current = undefined;
            return;
        }
        const rootEl = rootRef.current;
        if (!rootEl)
            return;
        const findFocusable = ignoreDisplayNone
            ? findClosestFocusableIgnoringDisplayNone
            : findClosestFocusable;
        if (returnCandidates.current === undefined) {
            // Snapshot the focus-return candidates when the dialog opens, most recent first. We keep the
            // RAW elements and defer resolving them to focusable until close: a candidate can be
            // momentarily disconnected right now — e.g. it lives in a parent dialog that hid itself when
            // this dialog opened — yet be reconnected by the time we close (the parent restores). Resolving
            // eagerly here would drop those candidates and lose the natural focus target.
            returnCandidates.current = getFocusReturnCandidates();
        }
        // React's `autoFocus` attribute will cause a child to receive focus before this effect
        // runs, so check if focus is already within this element before moving it somewhere else.
        if (!isFocusWithin(rootEl)) {
            const toFocus = rootEl.querySelector('[autofocus]') || rootEl;
            toFocus.focus();
        }
        return () => {
            // If focus already left the dialog, don't try to move it back. This can happen if there are
            // multiple dialogs mounted under the same parent and the entire tree is unmounting together,
            // like in multi page dialogs when the X button is clicked.
            //
            // Except if the default element is focused, which means focus wasn't explicitly moved,
            // it was just restored by the browser when this dialog was removed from the DOM.
            const currentRootEl = rootRef.current;
            if ((!currentRootEl || !isFocusWithin(currentRootEl)) && !isDefaultElementFocused())
                return;
            const candidates = returnCandidates.current;
            if (candidates) {
                // React does this weird thing where it stops firing synthetic events while unmounting,
                // which can break other code that expects `onFocus` events to fire reliably. By doing this
                // in a microtask, we ensure the focus happens after React has restored synthetic events.
                // Note that `setTimeout(..., 0)` would fire too late here, potentially allowing other focus
                // calls to make it in before ours.
                //
                // Resolving in the microtask (rather than synchronously here) also means we evaluate
                // connectedness *after* this commit finishes — so a candidate inside a parent dialog that
                // re-mounts as this dialog closes is correctly seen as connected and preferred over the
                // outer fallback.
                queueMicrotask(() => {
                    // Walk most-recent-first and focus the nearest candidate that is connected and focusable.
                    for (const candidate of candidates) {
                        if (!candidate.isConnected)
                            continue;
                        // For multi page dialogs the returning element may still be inside an inert tree; let
                        // the caller make it focusable (e.g. by clearing `inert` on ancestors) before we test
                        // focusability and move focus.
                        onBeforeFocusReturn?.(candidate);
                        const focusable = findFocusable(candidate);
                        if (!focusable)
                            continue;
                        focusable.focus();
                        return;
                    }
                });
            }
        };
        // the caller guarantees that `ref` will never change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const focusRemovalRef = useDetectFocusRemoval(() => {
        if (LastInteraction.key !== 'Tab') {
            // Focus is leaving the dialog but not going to another element. This can happen if a child
            // blurs itself on `Escape`. In this case let's grab focus and put it back on the dialog
            // so that the next `Escape` key press closes it.
            rootRef.current?.focus();
        }
    }, { enabled: open });
    const onBlur = (e) => {
        // A blur on a child Window can bubble up through the React DOM and hit the parent, incorrectly
        // stealing focus away from the child. Only handle events coming from the actual DOM subtree.
        if (isPortaledEvent(e))
            return;
        // Don't rescue focus if the whole browser window lost focus (e.g. the user switched apps or
        // tabs). The blur still fires with no `relatedTarget`, but the element didn't really lose
        // focus within the page, the window did. Stealing focus here could move focus away from the
        // active element, landing the user in an unexpected place when they return to the page.
        if (!document.hasFocus())
            return;
        if (!e.relatedTarget && LastInteraction.key !== 'Tab') {
            // Focus is leaving the dialog but not going to another element. This can happen if a child
            // blurs itself on `Escape`. In this case let's grab focus and put it back on the dialog
            // so that the next `Escape` key press closes it.
            //
            // TODO: should we instead close the dialog here? Ideally none of our components actually
            // bake in blur on Escape and that's part of a higher level hook we wrap things in.
            rootRef.current?.focus();
        }
    };
    const ref = useMergeRefs(refIn, rootRef, focusRemovalRef);
    return { ...props, ref, onBlur };
}

export { useFocusAndReturn };
//# sourceMappingURL=dialog-primitive.hooks.js.map
